import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './MainPage.css'; // Import the CSS file
import axiosInstance from '../api/axiosInstance';

// WGS84 to KMA grid conversion function
function dfs_xy_conv(code, v1, v2) {
    const RE = 6371.00877; // 지구 반경(km)
    const GRID = 5.0; // 격자 간격(km)
    const SLAT1 = 30.0; // 투영 위도1(degree)
    const SLAT2 = 60.0; // 투영 위도2(degree)
    const OLON = 126.0; // 기준점 경도(degree)
    const OLAT = 38.0; // 기준점 위도(degree)
    const XO = 43; // 기준점 X좌표(GRID)
    const YO = 136; // 기준점 Y좌표(GRID)

    const DEGRAD = Math.PI / 180.0;

    const re = RE / GRID;
    const slat1 = SLAT1 * DEGRAD;
    const slat2 = SLAT2 * DEGRAD;
    const olon = OLON * DEGRAD;
    const olat = OLAT * DEGRAD;

    let sn = Math.tan(Math.PI * 0.25 + slat2 * 0.5) / Math.tan(Math.PI * 0.25 + slat1 * 0.5);
    sn = Math.log(Math.cos(slat1) / Math.cos(slat2)) / Math.log(sn);
    let sf = Math.tan(Math.PI * 0.25 + slat1 * 0.5);
    sf = Math.pow(sf, sn) * Math.cos(slat1) / sn;
    let ro = Math.tan(Math.PI * 0.25 + olat * 0.5);
    ro = re * sf / Math.pow(ro, sn);
    const rs = {};
    if (code === "toXY") {
        rs['lat'] = v1;
        rs['lng'] = v2;
        let ra = Math.tan(Math.PI * 0.25 + (v1) * DEGRAD * 0.5);
        ra = re * sf / Math.pow(ra, sn);
        let theta = v2 * DEGRAD - olon;
        if (theta > Math.PI) theta -= 2.0 * Math.PI;
        if (theta < -Math.PI) theta += 2.0 * Math.PI;
        theta *= sn;
        rs['x'] = Math.floor(ra * Math.sin(theta) + XO + 0.5);
        rs['y'] = Math.floor(ro - ra * Math.cos(theta) + YO + 0.5);
    }
    return rs;
}

const MainPage = () => {
  const navigate = useNavigate();
  const [companyInfo, setCompanyInfo] = useState({});
  const [ceoName, setCeoName] = useState('');
  const [weather, setWeather] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState('내 위치');

  const regions = ['내 위치', '서울/경기', '부산/경남', '제주', '강원', '충청', '전라', '경상'];

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const companyInfoRes = await axiosInstance.get('/company-info');
        setCompanyInfo(companyInfoRes.data);

        const staffRes = await axiosInstance.get('/staff');
        const ceo = staffRes.data.find(s => s.role === 'CEO');
        if (ceo) {
          setCeoName(ceo.name);
        }
      } catch (error) {
        console.error('Error fetching footer data:', error);
      }
    };

    fetchFooterData();
  }, []);

  useEffect(() => {
    const fetchWeather = async () => {
      setWeather(null); // Reset weather on new fetch
      try {
        let response;
        if (selectedRegion === '내 위치') {
          navigator.geolocation.getCurrentPosition(async (position) => {
            const lat = position.coords.latitude;
            const lon = position.coords.longitude;
            const grid = dfs_xy_conv("toXY", lat, lon);
            response = await axiosInstance.get(`/weather?nx=${grid.x}&ny=${grid.y}`);
            setWeather(response.data);
          }, () => {
            setWeather({ error: "위치 정보 접근이 거부되었습니다." });
          });
        } else {
          response = await axiosInstance.get(`/weather/by-region?region=${selectedRegion}`);
          setWeather(response.data);
        }
      } catch (error) {
        console.error("Error fetching weather:", error);
        const errorMessage = error.response?.data?.message || "날씨 정보를 불러오는 중 오류가 발생했습니다.";
        setWeather({ error: errorMessage });
      }
    };

    fetchWeather();
  }, [selectedRegion]);

  const frequentPages = [
    { name: '공사 관리', image: '/tasks.png', path: '/tasks' },
    { name: '공사 배치', image: '/schedule.png', path: '/schedule' },
    { name: '작업 지시', image: '/work-orders.png', path: '/work-orders' },
    { name: '협회 신고 관리', image: '/reports.png', path: '/association-reports' },
    { name: '통계/분석', image: '/statistics.png', path: '/statistics' },
    { name: '다운로드', image: '/downloads.png', path: '/downloads' },
  ];

  const workSites = [
    { name: '영배시스템', image: '/kepco.png', path: 'https://partners.kepco.co.kr/main/index.jsp?w2xPath=/login_cocmp.xml' },
    { name: '한국전기기술인협회', image: '/association.png', path: 'https://www.keea.or.kr/head/main/main.do' },
    { name: '다음메일', image: '/daum.png', path: 'https://accounts.kakao.com/login/?continue=https%3A%2F%2Fmail.daum.net#login' },
  ];

  const handleCardClick = (path) => {
    if (path.startsWith('http')) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(path);
    }
  };

  const WeatherDisplay = () => {
    if (!weather || weather.error || !weather.current || !weather.forecast || !weather.forecast.daily || weather.forecast.daily.length === 0) {
        return <p style={{textAlign: 'center'}}>{weather?.error || '날씨 정보를 불러올 수 없습니다.'}</p>;
    }

    const skyState = { '1': '맑음', '3': '구름많음', '4': '흐림' };
    const ptyState = { '0': '없음', '1': '비', '2': '비/눈', '3': '눈', '5': '빗방울', '6': '빗방울/눈날림', '7': '눈날림' };

    const getWeatherIcon = (skyCode, ptyCode) => {
        if (ptyCode && ptyCode !== '0') {
            if (ptyCode === '1' || ptyCode === '5') return '🌧️'; // Rain
            if (ptyCode === '2' || ptyCode === '6') return '🌧️❄️'; // Rain/Snow
            if (ptyCode === '3' || ptyCode === '7') return '❄️'; // Snow
        }
        if (skyCode) {
            if (skyCode === '1') return '☀️'; // Clear
            if (skyCode === '3') return '☁️'; // Mostly Cloudy
            if (skyCode === '4') return '🌥️'; // Cloudy
        }
        return '🌡️'; // Default
    };

    const getWeatherText = (skyCode, ptyCode) => {
        if (ptyCode && ptyCode !== '0') {
            return ptyState[ptyCode] || '강수';
        }
        if (skyCode) {
            return skyState[skyCode] || '하늘';
        }
        return '날씨';
    };

    const getWeatherIconFromWf = (wfText) => {
        if (wfText.includes('맑음')) return '☀️';
        if (wfText.includes('구름많음')) return '☁️';
        if (wfText.includes('흐림')) return '🌥️';
        if (wfText.includes('비')) return '🌧️';
        if (wfText.includes('눈')) return '❄️';
        if (wfText.includes('소나기')) return '🌦️';
        return '🌡️';
    };

    const current = weather.current;
    const todayForecastData = weather.forecast.daily[0]; // Today's data is the first item

    const combinedDailyForecasts = weather.forecast.daily.slice(1, 4);

    return (
        <div className="animated-weather-background" style={{
            display: 'flex',
            width: '100%',
            border: '1px solid #eee',
            borderRadius: '8px',
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
            {/* Left Section: Current Weather */}
            <div style={{
                flex: 1.5,
                padding: '20px',
                borderRight: '1px solid #eee',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-around',
                minWidth: '200px'
            }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ fontSize: '7rem' }}>{getWeatherIcon(current.SKY || todayForecastData.SKY, current.PTY)}</div>
                    <div style={{ fontSize: '1.0rem', marginTop: '1px', color: '#666' }}>
                        {getWeatherText(current.SKY || todayForecastData.SKY, current.PTY)}
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginRight: '10px' }}>{weather.regionName}</div>
                        <select value={selectedRegion} onChange={(e) => setSelectedRegion(e.target.value)} style={{ padding: '2px 4px', borderRadius: '4px' }}>
                            {regions.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold' }}>{current.T1H}°C</div>
                    <div style={{ fontSize: '1rem', marginTop: '5px' }}>
                        {todayForecastData.POP && `강수확률 ${todayForecastData.POP}%`}
                    </div>
                    <div style={{ fontSize: '1rem', marginTop: '10px' }}>
                        {todayForecastData.TMN && todayForecastData.TMX && `최저 ${todayForecastData.TMN}°C / 최고 ${todayForecastData.TMX}°C`}
                    </div>
                </div>
            </div>

            {/* Right Section: 3-Day Forecast */}
            <div style={{ flex: 1, padding: '20px', display: 'flex', flexDirection: 'column' }}>
                <h4 style={{ textAlign: 'center', marginBottom: '15px', color: '#333' }}></h4>
                <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap' }}>
                    {combinedDailyForecasts.map((data, index) => {
                        const date = new Date();
                        date.setDate(date.getDate() + (index + 1));
                        const dayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
                        const displayDate = `${date.getMonth() + 1}/${date.getDate()}(${dayOfWeek})`;

                        const minTemp = data.TMN || data.taMin;
                        const maxTemp = data.TMX || data.taMax;
                        const pop = data.POP || data.rnSt;
                        const skyCode = data.SKY;
                        const ptyCode = data.PTY;
                        const wfText = data.wf;

                        return (
                            <div key={displayDate} style={{ textAlign: 'center', flex: 1, margin: '0 5px', minWidth: '100px', maxWidth: '150px' }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#555' }}>{displayDate}</div>
                                <div style={{ fontSize: '2rem', marginBottom: '5px' }}>
                                    {wfText ? getWeatherIconFromWf(wfText) : getWeatherIcon(skyCode, ptyCode)}
                                </div>
                                <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                    {minTemp && maxTemp && `${minTemp}°C / ${maxTemp}°C`}
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#888' }}>
                                    {wfText || getWeatherText(skyCode, ptyCode)}
                                </div>
                                {pop && <div style={{ fontSize: '0.8rem', color: '#888' }}>강수 {pop}%</div>}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
  };

  return (
    <>
      <div className="main-image-section">
        <img src="/banner.png" alt="Main Banner" className="main-banner-image" />
      </div>

      <div className="main-page-container">
        <div className="frequent-pages-section">
          <div className="card-container">
            <div className="card-grid">
              {frequentPages.map((page, index) => (
                <div key={index} className="page-card" onClick={() => handleCardClick(page.path)}>
                  <img src={page.image} alt={page.name} className="card-image" />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="weather-section" style={{marginTop: '20px'}}>
          <WeatherDisplay />
        </div>

        <div className="work-sites-section">
          <h3>업무 관련 사이트</h3>
          <hr/><br/>
          <div className="logo-container">
            {workSites.map((site, index) => (
              <a key={index} href={site.path} target="_blank" rel="noopener noreferrer" className="logo-link">
                <img src={site.image} alt={site.name} className="logo-image" />
              </a>
            ))}
          </div>
        </div>
      </div>

      <footer className="main-page-footer">
        <div className="footer-content">
          <p>(주)전우종합기술 | 대표: {ceoName || '정보 없음'} | 사업자등록번호: {companyInfo.businessRegistrationNumber || '정보 없음'}</p>
          <p>주소: {companyInfo.address || '정보 없음'} | 연락처: {companyInfo.phoneNumber || '정보 없음'}</p>
          <p>Copyright &copy; 2025 JUNWOO TECH. All Rights Reserved.</p>
        </div>
      </footer>
    </>
  );
};

export default MainPage;
